import type { Uuid } from '../types/api';
import type {
  ClientEventType,
  ConnectionState,
  ErrorFrame,
  ServerFrame,
} from '../types/websocket';
import { encodeBase64Url } from '../base64';
import { newUuid } from '../platform';
import { createCommand, isDurableEvent, parseServerFrame } from './events';
import { reconnectDelay, shouldReconnect } from './reconnect';

export interface RealtimeClientOptions {
  /** Returns a currently valid access token, refreshing it if necessary. */
  accessToken: () => Promise<string | null>;
  /** Absolute or relative WebSocket endpoint. */
  url?: string;
  subprotocolPrefix?: string;
  onEvent: (frame: ServerFrame) => void;
  onState?: (state: ConnectionState) => void;
  /** Called when the socket cannot replay the gap and REST must fill it in. */
  onResyncRequired?: (currentSequence: number) => void;
  socketFactory?: (url: string, protocols: string[]) => WebSocket;
  /** Where the last applied durable sequence is kept between page loads. */
  cursorStorage?: CursorStorage;
}

export interface CursorStorage {
  read(): number;
  write(sequence: number): void;
}

/** Keeps the cursor for the lifetime of the tab only. */
export class MemoryCursorStorage implements CursorStorage {
  private sequence = 0;
  read(): number {
    return this.sequence;
  }
  write(sequence: number): void {
    this.sequence = sequence;
  }
}

/**
 * Encode an access token so it is a legal WebSocket subprotocol name.
 *
 * base64url keeps the result inside the RFC 6455 token grammar; the padding is
 * stripped because `=` is a separator there. The token is ASCII, so no UTF-8
 * pass is needed.
 */
export function toSubprotocolToken(token: string): string {
  return encodeBase64Url(token);
}

interface PendingCommand {
  resolve: (payload: Record<string, unknown>) => void;
  reject: (error: Error) => void;
  timer: ReturnType<typeof setTimeout>;
}

const ACK_TIMEOUT_MS = 15_000;

/**
 * The single WebSocket connection for the whole application.
 *
 * No component opens its own socket. One connection per tab means the server
 * sees one presence entry per tab rather than one per mounted component, the
 * durable event cursor advances in one place, and a reconnect is a single
 * event the whole interface reacts to instead of a dozen racing ones.
 *
 * The cursor discipline is the important part: an event is only recorded as
 * applied after it has been handed to the application, and a gap in the
 * sequence triggers a resume rather than optimistic guessing about what was
 * missed.
 */
export class RealtimeClient {
  private socket: WebSocket | null = null;
  private state: ConnectionState = 'idle';
  private stopped = true;
  private resuming = false;
  private reconnectAttempt = 0;
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly pending = new Map<Uuid, PendingCommand>();
  private readonly cursor: CursorStorage;

  constructor(private readonly options: RealtimeClientOptions) {
    this.cursor = options.cursorStorage ?? new MemoryCursorStorage();
  }

  get connectionState(): ConnectionState {
    return this.state;
  }

  get lastSequence(): number {
    return this.cursor.read();
  }

  isReady(): boolean {
    return this.socket?.readyState === WebSocket.OPEN && !this.resuming;
  }

  start(): void {
    this.stopped = false;
    void this.connect();
  }

  stop(): void {
    this.stopped = true;
    this.clearTimers();
    this.socket?.close(1000, 'client stopped');
    this.socket = null;
    this.setState('idle');
  }

  /** Send a command and resolve when the server acknowledges that exact request. */
  async send<TResponse extends Record<string, unknown>>(
    type: ClientEventType,
    payload: Record<string, unknown>,
  ): Promise<TResponse> {
    if (!this.isReady() || !this.socket) {
      throw new Error('Realtime-соединение недоступно.');
    }
    const requestId = newUuid();
    const frame = createCommand(type, payload, requestId);
    const acknowledgement = new Promise<TResponse>((resolve, reject) => {
      const timer = setTimeout(() => {
        this.pending.delete(requestId);
        reject(new Error('Сервер не подтвердил команду.'));
      }, ACK_TIMEOUT_MS);
      this.pending.set(requestId, {
        resolve: resolve as (payload: Record<string, unknown>) => void,
        reject,
        timer,
      });
    });
    this.socket.send(JSON.stringify(frame));
    return await acknowledgement;
  }

  /** Fire and forget, for signals whose acknowledgement nothing waits on. */
  trySend(type: ClientEventType, payload: Record<string, unknown>): boolean {
    if (!this.isReady() || !this.socket) return false;
    this.socket.send(JSON.stringify(createCommand(type, payload)));
    return true;
  }

  private async connect(): Promise<void> {
    if (this.stopped || this.socket) return;
    this.setState('connecting');
    const token = await this.options.accessToken();
    if (this.stopped) return;
    if (!token) {
      // No credential means there is nothing to reconnect with; the
      // application has to send the user through sign-in.
      this.setState('revoked');
      return;
    }

    const url = this.resolveUrl();
    const prefix = this.options.subprotocolPrefix ?? 'liniya.bearer.';
    // The browser WebSocket API cannot set headers, so the credential travels
    // as a subprotocol. That keeps it out of the URL and therefore out of
    // access logs and browser history.
    //
    // It has to be encoded first: a subprotocol name must be an RFC 6455
    // token, and a signed token contains colons, which that grammar forbids.
    // Passing it raw makes the constructor throw before anything is sent.
    const protocols = [`${prefix}${toSubprotocolToken(token)}`];
    const socket = this.options.socketFactory
      ? this.options.socketFactory(url, protocols)
      : new WebSocket(url, protocols);
    this.socket = socket;

    socket.addEventListener('open', () => this.setState('authenticating'));
    socket.addEventListener('message', (event) => this.receive(event.data));
    // 1006 is what the standard calls an abnormal closure: some platforms
    // report no code at all when the connection simply disappears.
    socket.addEventListener('close', (event) => this.closed(event.code ?? 1006));
    socket.addEventListener('error', () => this.setState('unavailable'));
  }

  private resolveUrl(): string {
    if (this.options.url?.startsWith('ws')) return this.options.url;
    // Only a browser has a current page to take the host from. A native client
    // has to say where the server is, and saying so plainly beats silently
    // dialling localhost and then reporting the network as broken.
    const location = (globalThis as { location?: { protocol?: string; host?: string } }).location;
    if (!location?.host) {
      throw new Error('RealtimeClient: нужен абсолютный адрес ws:// или wss://.');
    }
    const path = this.options.url ?? '/ws/v1/events/';
    const scheme = location.protocol === 'https:' ? 'wss' : 'ws';
    return `${scheme}://${location.host}${path}`;
  }

  private receive(raw: unknown): void {
    let frame: ServerFrame;
    try {
      frame = parseServerFrame(raw);
    } catch {
      this.socket?.close(4400, 'invalid server frame');
      return;
    }

    if (frame.type === 'session.ready') {
      this.resuming = true;
      this.startHeartbeat(frame as { payload: { heartbeat_seconds?: number } });
      this.sendRaw('session.resume', { last_sequence: this.cursor.read() });
      this.options.onEvent(frame);
      return;
    }

    if (frame.type === 'session.resumed') {
      this.resuming = false;
      this.reconnectAttempt = 0;
      this.setState('connected');
      this.settleAck(frame);
      return;
    }

    if (frame.type === 'error') {
      const error = frame as ErrorFrame;
      if (error.payload.code === 'resync_required') {
        this.resuming = false;
        this.setState('resync_required');
        const current = Number(error.payload.details?.current_sequence ?? 0);
        // The socket cannot bridge this gap. Let the application refill from
        // REST and then hand the socket a cursor it can continue from.
        this.options.onResyncRequired?.(current);
      }
      this.settleAck(frame);
      this.options.onEvent(frame);
      return;
    }

    if (isDurableEvent(frame)) {
      const sequence = (frame as { sequence: number }).sequence;
      const applied = this.cursor.read();
      if (sequence <= applied) return;
      if (sequence !== applied + 1) {
        // Out of order or a hole: ask for the missing span rather than
        // applying an event whose predecessors were never seen.
        if (!this.resuming) {
          this.resuming = true;
          this.sendRaw('session.resume', { last_sequence: applied });
        }
        return;
      }
      this.options.onEvent(frame);
      this.cursor.write(sequence);
      return;
    }

    this.settleAck(frame);
    this.options.onEvent(frame);
  }

  /** Resolve whichever command was waiting on this response. */
  private settleAck(frame: ServerFrame): void {
    const requestId = (frame as { request_id?: string }).request_id;
    if (!requestId) return;
    const waiting = this.pending.get(requestId);
    if (!waiting) return;
    clearTimeout(waiting.timer);
    this.pending.delete(requestId);
    if (frame.type === 'error') {
      waiting.reject(new Error((frame as ErrorFrame).payload.message));
      return;
    }
    waiting.resolve((frame as { payload: Record<string, unknown> }).payload);
  }

  private startHeartbeat(frame: { payload: { heartbeat_seconds?: number } }): void {
    const seconds = Math.max(5, Number(frame.payload.heartbeat_seconds ?? 25));
    this.clearHeartbeat();
    this.heartbeatTimer = setInterval(() => this.sendRaw('heartbeat.ping', {}), seconds * 1000);
  }

  private sendRaw(type: ClientEventType, payload: Record<string, unknown>): void {
    if (this.socket?.readyState !== WebSocket.OPEN) return;
    this.socket.send(JSON.stringify(createCommand(type, payload)));
  }

  private closed(closeCode: number): void {
    this.clearHeartbeat();
    this.socket = null;
    this.resuming = false;
    this.rejectPending('Соединение закрыто.');

    if (this.stopped || !shouldReconnect(closeCode)) {
      this.setState(closeCode === 4409 || closeCode === 4401 ? 'revoked' : 'disconnected');
      return;
    }
    this.setState('reconnecting');
    const delay = reconnectDelay(this.reconnectAttempt);
    this.reconnectAttempt += 1;
    this.reconnectTimer = setTimeout(() => void this.connect(), delay);
  }

  private rejectPending(reason: string): void {
    for (const [, waiting] of this.pending) {
      clearTimeout(waiting.timer);
      waiting.reject(new Error(reason));
    }
    this.pending.clear();
  }

  private clearHeartbeat(): void {
    if (this.heartbeatTimer) clearInterval(this.heartbeatTimer);
    this.heartbeatTimer = null;
  }

  private clearTimers(): void {
    this.clearHeartbeat();
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;
  }

  private setState(state: ConnectionState): void {
    if (this.state === state) return;
    this.state = state;
    this.options.onState?.(state);
  }

  /** Move the cursor after a REST resync filled the gap the socket could not. */
  acceptCursor(sequence: number): void {
    this.cursor.write(sequence);
    this.resuming = false;
    if (this.isReady()) this.setState('connected');
  }

  /**
   * Reconnect immediately, for the moments a browser hands back control:
   * the tab becoming visible again, or the machine coming back online.
   */
  reconnectNow(): void {
    if (this.stopped) return;
    if (this.socket?.readyState === WebSocket.OPEN) return;
    this.clearTimers();
    this.reconnectAttempt = 0;
    void this.connect();
  }
}
